<template>
    <div class="page__content booking-detail">
        <div v-if="errorCode">
            <div class="booking-detail__section" v-if="errorCode == 404">
                <div class="columns is-multiline">
                    <div class="column is-12">
                        <h2 class="page__headline">{{ $t('bookingDetails.error.bookingNotFound') }}</h2>
                    </div>
                    <div class="column is-12">
                        <router-link class="button no-print" exact :to="{ name: 'bookings', params: {lang: lang} }">
                            {{ $t('bookingDetails.backToBookingOverview') }}
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
        <div class="edit-box box-shadow" v-else>
            <div class="booking-detail__section">
                <div class="columns is-tablet is-tablet-reverse">
                    <div class="column is-4-tablet no-print">
                        <div class="is-clearfix">
                            <a class="button is-primary booknow is-large no-print" @click="openModal('accept')"
                               v-if="booking.booking_status === 'pending'">
                                <span class="icon">
                                    <i class="fa fa-check-circle" aria-hidden="true"></i>
                                </span>
                                <span>{{ $t('bookingDetails.bookings.accept') }}</span>
                            </a>
                            <router-link class="button overview" exact :to="{ name: 'bookings', params: {lang: lang} }">
                                {{ $t('bookingDetails.backToBookingOverview') }}
                            </router-link>
                        </div>
                    </div>
                    <div class="column is-8-tablet">
                        <h2 class="page__headline is-inline-block">{{ $tc('generic.menu.bookings.bookings', 1) }} {{ booking.booking_number }}</h2>
                        <div class="page__headline__tag-wrapper is-inline-block">
                            <span class="page__headline__tag tag is-primary" v-if="booking.is_new"> {{ $t('generic.new') }} </span>
                            <span class="page__headline__tag tag" v-if="booking.booking_status && booking.booking_status !== 'confirmed'"
                                  :class="{
                                    'is-primary': booking.booking_status === 'confirmed',
                                    'is-warning': booking.booking_status === 'pending',
                                    'is-danger': booking.booking_status === 'canceled'
                                    }">
                                {{ $t(`bookingDetails.status.${booking.booking_status}`) }}
                            </span>
                            <span class="page__headline__tag tag is-primary" v-if="booking.is_paid_out_to_campground">{{ $t('bookingDetails.status.paidOut') }}</span>
                        </div>
                    </div>
                </div>
                <div class="columns is-multiline no-print">
                    <div class="column is-12" v-if="false">
                        <div class="is-clearfix">
                            <div class="booking__modified-wrapper is-pulled-left">
                                <span class="booking__modified__label">{{ $t('bookingDetails.status.markAsModified') }}:</span>
                                <a class="button booking__modified" @click="modifiedBooking()">
                                    <span class="icon">
                                        <i class="fa fa-check-circle is-valid" aria-hidden="true" v-if="booking.is_processed"></i>
                                        <i class="fa fa-ban" aria-hidden="true" v-if="!booking.is_processed"></i>
                                    </span>
                                    <span v-if="!booking.is_processed">{{ $t('bookingDetails.status.untouched') }}</span>
                                    <span v-if="booking.is_processed">{{ $t('bookingDetails.status.modified') }}</span>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="column is-4">
                        <h5 class="subtitle is-5">{{ $t('bookingDetails.bookingDate') }}:</h5>
                        <p>{{ booking.created_at | localizedDate('L, LTS') }}</p>
                    </div>
                    <div class="column is-4">
                        <h5 class="subtitle is-5">{{ $t('bookingDetails.portalHeader') }}:</h5>
                        <p>{{ this.portal_or_affiliate() }}</p>
                        <p v-if="this.portalBookingId()">{{ $t("bookingDetails.portalId") }}: {{ this.portalBookingId() }}</p>
                    </div>
                    <div class="column is-4" v-if="booking.pms_data">
                        <h5 class="subtitle is-5 is-inline-block">{{ $t('generic.pmsHeadLine') }}:</h5>
                         <table class="table is-bordered is-striped is-narrow">
                             <ul>
                                <li>{{ $t('generic.pmsSystem')}}: {{ booking.pms_data.pms }}</li>
                                <li v-if="booking.pms_data.booking_id">{{ $t('generic.pmsBookingId')}}: {{ booking.pms_data.booking_id }}</li>
                                <li v-if="booking.pms_data.booking_link">
                                  {{ $t('generic.pmsBookingLink')}}:
                                  <a :href="booking.pms_data.booking_link" target="_blank">
                                    {{ booking.pms_data.booking_id }}
                                  </a>
                                </li>
                             </ul>
                         </table>
                    </div>
                </div>
            </div>

            <div class="booking-detail__section" v-if="booking.cancelation_reason || booking.cancellation_term">
                <div class="columns">

                    <div class="column is-6">
                        <h5 class="subtitle is-5 is-inline-block">
                           {{ $t('cancellationTerms.cancellationHeader') }}:
                        </h5>
                        <div v-if="booking.cancellation_term">
                            {{ booking.cancellation_term.pms_key}} : {{ booking.cancellation_term.name}}<br>
                            <ul>
                                <li v-for="item in booking.cancellation_term.cancellation_summary">
                                    {{ item}}
                                </li>
                            </ul>
                            <br>
                            <ul>
                                <li v-for="item in booking.cancellation_term.fees">
                                    {{ item.from_date | localizedDate('L')}} -
                                    {{ item.to_date | localizedDate('L')}} :
                                    {{ item.fee | currency($getDefaultCurrency().code, lang) }}
                                </li>
                            </ul>
                            <br>
                            <div v-if="!booking.canceled_at || booking.booking_status !== 'canceled'">{{ $t('cancellationTerms.fee_now') }}: {{booking.cancellation_fee_now | currency($getDefaultCurrency().code, lang) }}</div>
                        </div>
                    </div>


                    <div class="column is-6" v-if="booking.canceled_at || booking.booking_status === 'canceled'">
                        <h5 class="subtitle is-5">{{ $t('bookingDetails.cancelHeadline') }}:</h5>
                        <p class="booking-detail__cancel-reason">{{ booking.cancelation_reason }}</p>
                        <p>{{ $t('cancellationTerms.canceled_at') }} {{ booking.canceled_at | localizedDate('L LTS')}}, {{ $t('cancellationTerms.fee') }}: {{ booking.cancellation_fee | currency($getDefaultCurrency().code, lang) }}</p>
                    </div>
                </div>
            </div>
            <hr class="booking-detail__seperator">
            <div class="booking-detail__section">
                <div class="columns is-multiline">
                    <div class="column is-half">
                        <div class="booking__address">
                            <h5 class="subtitle is-5">{{ $t('bookingDetails.bookingData') }}:</h5>
                            <p class="booking__address__standard">
                                {{ booking.first_name }} {{ booking.last_name }}
                                <span class="is-block">{{ booking.street1 }}</span>
                                <span class="is-block">{{ booking.street2 }}</span>
                                <span class="is-block">{{ booking.zip_code }} {{ booking.city }}</span>
                                <span class="is-block">{{ booking.country_name }}</span>
                            </p>
                            <p class="booking__address__additional">
                                <a :href="'tel:'+booking.phone" class="is-block" v-if="booking.phone">{{ booking.phone }}</a>
                                <a :href="'mailto:'+booking.email" class="is-block" v-if="booking.email">{{ booking.email }}</a>
                            </p>
                        </div>
                    </div>
                    <div class="column is-half">
                        <h5 class="subtitle is-5">{{ $t('bookings.period') }}:</h5>
                        <div class="season-time-wrapper">
                            <span class="season-time-start">{{ booking.booking_data.date_from | localizedDate('L') }}</span>
                            <span class="season-time-spacer">bis</span>
                            <span class="season-time-start">{{ booking.booking_data.date_until | localizedDate('L') }}</span>
                            <span class="season-time-spacer">({{ numberOfNights }} {{ $t('bookingDetails.nights') }})</span>
                        </div>
                        <div class="booking-details__guests">
                            <h5 class="subtitle is-5">
                                {{ $t('bookingDetails.guests') }} ({{ booking.num_persons }}):
                            </h5>
                            <ul class="booking-details__guests-list">
                                <li class="booking-details__guests-list-item" v-for="guest in booking.guests">
                                    {{ guest.first_name }} {{ guest.last_name }} (<span v-if="guest.age">{{ guest.age }} {{ $t('bookings.years') }}</span><span v-if="guest.birthdate">{{ guest.birthdate | localizedDate('L') }}</span><span v-else>{{ guest.birthyear }}</span>)</li>
                            </ul>
                        </div>
                    </div>

                    <div class="column is-half" v-if="booking.license_plate">
                        <h5 class="subtitle is-5">{{ $t('bookings.licensePlate') }}:</h5>
                        <div><div class="license-plate"><div class="inner">{{ booking.license_plate }}</div></div></div>
                    </div>
                </div>
            </div>
            <hr class="booking-detail__seperator" />
            <div class="booking-detail__section">
                <div class="columns is-desktop is-multiline">
                    <div class="column is-12">
                        <h5 class="subtitle is-5" style="margin: 0;">{{ $t('bookingDetails.bookedHousing') }}:</h5>
                    </div>
                    <div class="column" :class="{'is-half' : booking.booking_data.campsites.length === 1 }" v-for="data in booking.booking_data.campsites">
                        <div class="booking__booking-data">
                            <div class="booking__booking-data__amount">
                                <span class="booking__booking-data__campsite__count">{{ data.amount }} <small>x</small></span>
                                <div class="booking__booking-data__campsite__icon-wrapper">
                                    <img class="booking__booking-data__campsite__icon" width="130" :src="'/static/img/icons/icon_' + data.campsite_type + '.png'"/>
                                    <span  class="booking__booking-data__campsite__title">
                                        {{ data.name }}
                                    </span>
                                </div>
                            </div>
                            <table class="table is-bordered is-striped is-narrow">
                                <tbody>
                                    <tr v-for="person in data.persons" v-if="person.amount != 0">
                                        <td>{{ person.name }}</td>
                                        <td>{{ person.amount }} </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="booking-detail__seperator">

            <div class="booking-detail__section">
                <div class="columns">
                    <div class="column">
                        <h5 class="subtitle is-5 is-inline-block">
                            {{ $t('generic.bookedExtras') }}:
                            <span v-if="!getAllExtraComponents().length">{{ $t('generic.none') }}</span>
                        </h5>

                        <ul>
                            <li v-for="component in getOptionalExtraComponents()">
                                <span v-if="component.hr">{{ component.hr }}</span>
                                <span v-else>{{ component.description }}</span>
                            </li>
                            <li v-for="component in getMandatoryExtraComponents()">
                                <span v-if="component.hr">{{ component.hr }}</span>
                                <span v-else>{{ component.description }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <hr class="booking-detail__seperator">

            <div class="booking-detail__section">
                <div class="columns">
                    <div class="column" :class="{'no-print': !notes }">
                        <h5 class="subtitle is-5 is-inline-block">
                            {{ $t('generic.note') }}:
                        </h5>
                        <div class="booking-detail__toggle" @click="toggleEditMode()">
                            <a class="button no-print" v-show="!isEditMode">
                                <i class="fa fa-pencil-square-o fa-lg" aria-hidden="true"></i>
                            </a>
                            <a class="button no-print" v-show="isEditMode">
                                <i class="fa fa-times fa-lg" aria-hidden="true"></i>
                            </a>
                        </div>
                        <wysiwyg class="campsite__season__wysiwyg" v-if="isEditMode" v-model="notes"
                                 :options="{maxChars: 100}"></wysiwyg>
                        <p class="booking-detail__note" v-show="!isEditMode && booking.notes"
                           v-html="booking.notes"></p>
                        <button v-show="isEditMode" class="button is-primary is-pulled-right" @click="saveNotes()">
                            {{ $t('generic.save') }}
                        </button>
                    </div>
                    <div class="column is-half">
                        <div class="booking__customer__message" v-if="booking.message">
                            <h5 class="subtitle is-5">{{ $t('bookingDetails.annotation') }}:</h5>
                            <p>{{ booking.message }}</p>
                        </div>
                        <div class="booking__customer__camper-info" :class="{'has-margin': booking.message }"
                             v-if="booking.vehicle_width">
                            <h5 class="subtitle is-5">{{ $t('bookingDetails.headlineCamperSize') }}:</h5>
                            <p>{{ $t('bookingDetails.length') }}: {{ booking.vehicle_length }}m</p>
                            <p>{{ $t('bookingDetails.width') }}: {{ booking.vehicle_width }}m</p>
                        </div>
                        <div class="booking__customer__camper-info" :class="{'has-margin': booking.message }"
                             v-if="!booking.vehicle_width && booking.camper_sizes">
                            <h5 class="subtitle is-5">{{ $t('bookingDetails.headlineCamperSize') }}:</h5>
                            <p>{{ booking.camper_sizes }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="booking-detail__seperator">
            <div class="booking-detail__section">
                <div class="columns">
                    <div class="column">
                        <div class="booking__booking-prices" v-if="booking.pricelist">
                            <h5 class="subtitle is-5">{{ $t('bookingDetails.priceInfoHeadline') }}:</h5>
                            <table class="table is-bordered is-striped is-narrow">
                                <thead>
                                    <tr>
                                        <th>{{ $t('bookingDetails.nights') }}</th>
                                        <th>{{ $t('bookingDetails.count') }}</th>
                                        <th>{{ $t('bookingDetails.campsitePerson') }}</th>
                                        <th>{{ $t('bookingDetails.season') }}</th>
                                        <th>{{ $t('bookingDetails.pricePerNight') }}</th>
                                        <th>{{ $t('bookingDetails.price') }}</th>
                                    </tr>
                                </thead>
                                <tbody v-if="booking.pricelist">
                                <tr v-for="component in getAllNonExtraComponents()">
                                    <td>{{ component.days }}</td>
                                    <td v-if="component.object_amount">{{ component.object_amount }}</td>
                                    <td v-else>{{ component.amount }}</td>
                                    <td v-if="component.object_name">
                                        {{ component.object_name }}
                                        <span v-if="component.campsite_name">({{ component.campsite_name }})</span>
                                    </td>
                                    <td v-else>
                                        {{ component.name }}
                                        <span v-if="component.campsite_name">({{ component.campsite_name }})</span>
                                    </td>
                                    <td>{{ component.season_name }}</td>
                                    <td>
                                        <span class="tag is-light" v-if="component.price_per_day">{{ component.price_per_day | currency($getDefaultCurrency().code, lang) }}</span>
                                        <span class="tag is-light" v-else>{{ component.single_price | currency($getDefaultCurrency().code, lang) }}</span>
                                    </td>
                                    <td>
                                        <span class="is-pulled-right">{{ component.price | currency($getDefaultCurrency().code, lang)  }}</span>
                                    </td>
                                </tr>

                                <tr v-if="getOptionalExtraComponents().length">
                                    <th colspan="5" class="text-left">{{ $t('bookingDetails.extras') }}</th>
                                    <th>{{ $t('bookingDetails.price') }}</th>
                                </tr>

                                <tr v-for="component in getOptionalExtraComponents()" v-if="component.price > 0">
                                    <td colspan="4" class="text-left" v-if="component.hr">{{ component.hr }}</td>
                                    <td colspan="5" class="text-left" v-else>{{ component.description }}</td>
                                    <td><span class="is-pulled-right">{{ component.price | currency($getDefaultCurrency().code, lang) }}</span></td>
                                </tr>

                                <tr v-for="component in getOptionalExtraComponents()" v-if="component.price <= 0">
                                    <td colspan="5" class="text-left" v-if="component.hr">{{ component.hr }}</td>
                                    <td colspan="5" class="text-left" v-else>{{ component.description }}</td>
                                    <td><span class="is-pulled-right">{{ component.price | currency($getDefaultCurrency().code, lang) }}</span></td>
                                </tr>

                                <tr v-if="getMandatoryExtraComponents().length">
                                    <th colspan="5" class="text-left">{{ $t('bookingDetails.extracost') }}</th>
                                    <th>{{ $t('bookingDetails.price') }}</th>
                                </tr>

                                <tr v-for="component in getMandatoryExtraComponents()" v-if="component.price > 0">
                                    <td colspan="4" class="text-left" v-if="component.hr">{{ component.hr }}</td>
                                    <td colspan="5" class="text-left" v-else>{{ component.description }}</td>
                                    <td><span class="is-pulled-right">{{ component.price | currency($getDefaultCurrency().code, lang) }}</span></td>
                                </tr>

                                <tr v-for="component in getMandatoryExtraComponents()" v-if="component.price <= 0">
                                    <td colspan="5" class="text-left" v-if="component.hr">{{ component.hr }}</td>
                                    <td colspan="5" class="text-left" v-else>{{ component.description }}</td>
                                    <td><span class="is-pulled-right">{{ component.price | currency($getDefaultCurrency().code, lang) }}</span></td>
                                </tr>
                                <tr>
                                    <td colspan="5">
                                        <span class="booking__booking-prices--bold is-pulled-right">{{ $t('bookingDetails.priceTotal') }}:</span>
                                    </td>
                                    <td>
                                        <span class="booking__booking-prices--bold is-pulled-right">
                                            <nobr>{{ booking.pricelist.total | currency($getDefaultCurrency().code, lang) }}</nobr>
                                        </span>
                                    </td>
                                </tr>

                                <tr>
                                    <td colspan="11" class="price-disclaimer"><span class="is-pulled-right">{{ $t('bookingDetails.priceDisclaimer') }}</span></td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="booking-detail__section">
                <div class="columns">
                    <div class="column">
                        <h5 class="subtitle is-5 is-inline-block">{{ $t('generic.paymentHeadLine') }}:</h5>
                        <table class="table is-bordered is-striped is-narrow">
                            <thead>
                                <tr>
                                    <th>Provider</th>
                                    <th>Provider ID</th>
                                    <th>Datum</th>
                                    <th>Status</th>
                                    <th>Betrag</th>
                                    <th>Beleg</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(payment, index) in booking.payments" :key="index">
                                    <td>{{ payment.provider }}</td>
                                    <td>{{ payment.payment_provider_id }}</td>
                                    <td>{{ payment.created_at }}</td>
                                    <td>{{ payment.status }}</td>
                                    <td>{{ payment.price | currency($getDefaultCurrency().code, lang) }}</td>
                                    <td v-if="payment.provider ==='STRIPE'"><a :href="payment.intent_received.charges.data[0].receipt_url" target="_blank">Beleg</a></td>
                                    <td v-else></td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr style="font-weight: bold">
                                    <td colspan="4">Summe</td>
                                    <td>{{ booking.paid | currency($getDefaultCurrency().code, lang) }}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>



            <div class="booking-detail__section" v-if="booking.pms_data">
                <div class="columns">
                    <div class="column">
                        <h5 class="subtitle is-5 is-inline-block">{{ $t('generic.pmsHeadLine') }}:</h5>
                         <table class="table is-bordered is-striped is-narrow">
                             <tr>
                                <td>{{ $t('generic.pmsSystem')}}: {{ booking.pms_data.pms }}</td>
                                <td>{{ $t('generic.pmsBookingId')}}: {{ booking.pms_data.booking_id }}</td>
                                <td>
                                  {{ $t('generic.pmsBookingLink')}}:
                                  <a v-if="booking.pms_data.booking_link" :href="booking.pms_data.booking_link" target="_blank">
                                    {{ booking.pms_data.booking_id }}
                                  </a>
                                    <span v-else>-</span>
                                </td>
                             </tr>
                         </table>
                    </div>
                </div>
            </div>



            <div class="booking-detail__section is-clearfix no-print">
                <a class="button is-primary is-large is-pulled-left booking-detail__button-accept" @click="openModal('accept')" v-if="booking.booking_status === 'pending'">
                    <span class="icon">
                        <i class="fa fa-check-circle" aria-hidden="true"></i>
                    </span>
                    <span>{{ $t('bookingDetails.bookings.accept') }}</span>
                </a>
                <a class="button is-danger is-pulled-right booking-detail__button-cancel" @click="openModal('cancel')"
                   v-if="booking.booking_status !== 'canceled'">
                    <span class="icon">
                        <i class="fa fa-ban" aria-hidden="true"></i>
                    </span>
                    <span>{{ $t('bookingDetails.bookings.cancel') }}</span>
                </a>
                <a class="button is-pulled-right booking-detail__button-print" @click="printBooking()">
                    <span class="icon">
                        <i class="fa fa-print" aria-hidden="true"></i>
                    </span>
                    <span>{{ $t('bookingDetails.bookings.print') }}</span>
                </a>
            </div>
        </div>

        <div class="modal isActive no-print" :class="{ 'is-active': modal.isActive }">
            <div class="modal-background" @click="closeModal()"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ modal.title }}</p>
                    <button class="delete" @click="closeModal()"></button>
                </header><!-- force_cancel -->
                <section class="modal-card-body" v-if="modal.content">
                    <textarea v-if="modal.type === 'cancel'" class="textarea" v-model="modal.cancelReason" :placeholder="$t('bookingDetails.bookings.reasonPlaceholder')"></textarea>
                    <article v-if="modal.type === 'force_cancel'" class="message is-warning">
                        <div class="message-header">
                            <p>{{ this.$t('bookingDetails.refund.title') }}</p>
                        </div>
                        <div class="message-body">
                            {{ $t('bookingDetails.refund.notRefundableAndNotPaidOut') }}
                        </div>
                    </article>
                </section>
                <footer class="modal-card-foot is-clearfix">
                    <a class="button" @click="closeModal()">{{ $t('generic.cancel') }}</a>
                    <a v-if="modal.type === 'cancel'" class="button" :class="modal.buttonClass" @click="cancelBooking()" :disabled="!modal.cancelReason">
                        <span class="icon" >
                            <i class="fa fa-trash" aria-hidden="true"></i>
                        </span>
                        <span>{{ $t('bookingDetails.bookings.cancel') }}</span>
                    </a>
                    <a v-if="modal.type === 'force_cancel'" class="button" :class="modal.buttonClass" @click="cancelBooking(true)">
                        <span class="icon" >
                            <i class="fa fa-trash" aria-hidden="true"></i>
                        </span>
                        <span>{{ $t('bookingDetails.bookings.cancel') }}</span>
                    </a>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';

    import filters from '../../../filters';
    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import { EventBus, Tracking } from '../../../utils';
    import { Wysiwyg } from '../../components';

    const CANCEL = 'cancel';
    const FORCE_CANCEL = 'force_cancel';

    export default {
        name: 'booking-detail',

        props: ['bookingId'],

        filters: filters,

        components: {
            Wysiwyg
        },

        data() {
            return {
                errorCode: null,
                booking: {
                    booking_data: {
                    }
                },
                isEditMode: false,
                modal: {
                    isActive: false,
                    title: null,
                    cancelReason: null,
                },
                notes: null,
                moment: moment,
                numberOfNights: 0,
            };
        },

        computed: {
            lang() {
                return AppModel.lang;
            },

            depositIsTotal() {
                return this.booking.pricelist.deposit_value === this.booking.pricelist.total;
            },

            depositIsPercent() {
                return !this.depositIsTotal && this.booking.pricelist.deposit_value !== this.booking.pricelist.minimum_fixed_deposit;
            },
        },

        beforeRouteEnter(to, from, next) {
            Api.bootstrap().then(function(response) {
                return Promise.all([response, Api.bookings.fetchBookingDetail(to.params.bookingId)]);
            }).then(function(response) {
                next((vm) => {
                    if(response[1].ok) {
                        vm.onBookingFetched(response[1].body);
                    } else {
                        vm.onBookingFetchFailed(response[1].status);
                    }
                });
            });
        },

        created() {
            const action = this.$route.params.action;
            console.log('gk-debug', 'created', action);
            if(action === 'cancel') {
                this.openCancelModal();
            }
        },

        methods: {
            openCancelModal() {
                const selectedBooking = this.$route.params.bookingId;

                if(selectedBooking) {
                    // Buchung setzen, Modal öffnen und Aktion starten
                    this.selectedBooking = selectedBooking; // Buchung für das Modal bereitstellen
                    this.openModal(CANCEL); // Definiere CANCEL oder passe die Methode an
                } else {
                    console.warn('Keine Buchung gefunden, die storniert werden kann.');
                }
            },

            onBookingFetched(data) {
                this.booking = data;
                this.notes = data.notes ? (' ' + data.notes).slice(1) : null;

                this.numberOfNights = Math.abs(moment(this.booking.booking_data.date_until).diff(moment(this.booking.booking_data.date_from), 'days'));
            },

            onBookingFetchFailed(errorCode) {
                this.errorCode = errorCode;
            },

            toggleEditMode() {
                this.isEditMode = !this.isEditMode;
                this.notes = this.booking.notes;
            },

            saveNotes() {
                this.updateBooking({notes: this.notes});
                Tracking.trackEvent('BookingDetails', 'updateBooking', 'saveNotes');
            },

            closeModal() {
                this.modal.isActive = false;
                Tracking.trackEvent('BookingDetails', 'closeModal', this.modal.type);
            },

            getMandatoryExtraComponents() {
                if(this.booking.pricelist && this.booking.pricelist.components) {
                    return this.booking.pricelist.components.filter((component) => {
                        return this.isMandatoryExtra(component);
                    });
                }
                return [];
            },

            getOptionalExtraComponents() {
                if(this.booking.pricelist && this.booking.pricelist.components) {
                    return this.booking.pricelist.components.filter((component) => {
                        return this.componentIsExtra(component) && !this.isMandatoryExtra(component);
                    });
                }
                return [];
            },

            getAllExtraComponents() {
                if(this.booking.pricelist && this.booking.pricelist.components) {
                    return this.booking.pricelist.components.filter((component) => {
                        return this.componentIsExtra(component);
                    });
                }
                return [];
            },

            getAllNonExtraComponents() {
                if(this.booking.pricelist && this.booking.pricelist.components) {
                    return this.booking.pricelist.components.filter((component) => {
                        return !this.componentIsExtra(component);
                    });
                }
                return [];
            },

            componentIsExtra(component) {
                if(component.hasOwnProperty('extra') && component.extra !== undefined && component.extra !== null) {
                    return true;
                }
                return component.object_type === 'extra';
            },

            isMandatoryExtra(component) {
                if(!this.componentIsExtra(component)) {
                    return false;
                }
                if(component.hasOwnProperty('mandatory')) {
                    return !!component.mandatory;
                }
                if(component.hasOwnProperty('extra_fields') && component.extra_fields.hasOwnProperty('is_mandatory')) {
                    return !!component.extra_fields.is_mandatory;
                }
                return false;
            },

            parseStringToArray(string) {
                const correctedString = string.replace(/'/g, '"');
                return JSON.parse(correctedString);
            },

            getCancellationSummary(strSummary) {
                return this.parseStringToArray(strSummary);
            },

            openModal(type) {
                if(type === CANCEL) {
                    this.modal.type = CANCEL;
                    this.modal.title = this.$t('bookingDetails.bookings.cancelTitle');
                    this.modal.content = true;
                    this.modal.buttonClass = 'is-danger';
                }

                if(type === FORCE_CANCEL) {
                    this.modal.type = FORCE_CANCEL;
                    this.modal.title = this.$t('bookingDetails.bookings.cancelTitle');
                    this.modal.content = true;
                    this.modal.buttonClass = 'is-danger';
                }

                if(type !== CANCEL && type !== FORCE_CANCEL) {
                    this.modal.cancelReason = null;
                }

                this.modal.isActive = true;

                Tracking.trackEvent('BookingDetails', 'click', type);
            },

            modifiedBooking() {
                let data = {
                    'is_processed': !this.booking.is_processed
                };
                this.booking.is_processed = !this.booking.is_processed;

                Tracking.trackEvent('BookingDetails', 'updateProcess', 'is_processed', this.booking.is_processed);
                this.updateBooking(data);
            },

            cancelBooking(force) {
                console.log('cancelBooking:', force);
                let data = {
                    booking_status: 'canceled',
                    cancelation_reason: this.modal.cancelReason,
                    notes: this.notes,
                };

                if(force) data.force = 1;

                this.booking.is_processed = true;
                this.updateBooking(data);
                Tracking.trackEvent('BookingDetails', 'updateBooking', 'booking_status', 0);
            },

            updateBooking(data) {
                console.log('updateBooking:', data);
                let bookingId = this.booking.booking_id;

                Api.bookings.updateBookingDetail(bookingId, data)
                    .then((response) => {
                        if(response.ok) {
                            this.booking.notes = this.notes;
                            this.booking.booking_status = response.body.booking_status;
                            this.booking.cancelation_reason = response.body.cancelation_reason;
                            this.isEditMode = false;
                            this.onUpdateDone();
                        } else {
                            this.onUpdateFailed(response);
                        }
                    }, () => {
                        this.onUpdateFailed();
                    });

                if(this.modal.isActive) {
                    this.closeModal();
                }
            },

            onUpdateFailed(response = null) {
                if(response) {
                    if(response.body && response.body.indexOf('not-refundable') !== -1) {
                        this.openModal('force_cancel');
                        return;
                    } else {
                        console.log('ERROR:', response.body);
                        this.apiErrors = response.body;
                    }
                }
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`, direction: 'Up', type: 'danger', body: this.$t('generic.formError') });
                Tracking.trackEvent('BookingDetails', 'updateBooking', 'failed', null);
            },

            onUpdateDone() {
                this.isEditMode = false;
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
                Tracking.trackEvent('BookingDetails', 'updateBooking', 'success', null);

                EventBus.$emit(EventBus.BOOTSTRAP_RESET);
            },

            printBooking() {
                window.print();
            },

            portalBookingId() {
                if(!this.booking.portal_booking_id) {
                    return '';
                }
                if(this.booking.portal_sub_info) {
                    return this.booking.portal_booking_id + ' / ' + this.booking.portal_sub_info;
                }
                return this.booking.portal_booking_id;
            },

            portal_or_affiliate() {
                if(this.booking.portal) {
                    return this.booking.portal;
                }
                if(this.booking.affiliate) {
                    return this.booking.affiliate.name;
                }
                return '';
            },

            getEuroPrice(value) {
                if(!value) {
                    return '0,00';
                }
                let res = String(value).split('.');
                if(res[1]) {
                    res[1] = res[1].length < 2 ? res[1] + '0' : res[1];
                } else {
                    res.push('00');
                }
                return res[0] + ',' + res[1];
            },
        },
    };
</script>

<style lang="scss">
    @import "booking-details.scss";
</style>



// WEBPACK FOOTER //
// src/views/pages/booking-detail/bookingDetail.vue